/**
 * Where a split ticket stands in its split (ENG-3033), read off what the split
 * wrote on it (`source_ref`, `workItems/pageActs.ts`): the ticket it came out
 * of, its place in the order, and what it waits on to merge. A child used to
 * show only "Queued", with nothing tying it to its siblings or its parent.
 * A child built ahead (YAG-1112) plans and builds at once, and only its merge
 * waits on the one before it.
 */
import {
  splitLeadOf,
  type WorkItem,
  type WorkItemSplitPart,
  type WorkItemSplitProgress,
  type WorkPipelineStage,
} from "@yagni/shared";
import type { StagePieMode } from "../ui/stagePie.js";

export interface SplitPlace {
  parentId: string;
  /** Null on a child split before its parent's title was kept. */
  parentTitle: string | null;
  /** The parent's tracker key, else its own; null when neither was kept. */
  parentKey: string | null;
  order: number | null;
  /** Its part of the split, ordered or not: `splitOrder`, else `splitPart` (YAG-1111). */
  part: number | null;
  total: number | null;
  beside: boolean;
  /**
   * The sibling whose merge this one waits on: to start, or, built ahead, to
   * merge itself. Null once that sibling merged.
   */
  waitsOn: string | null;
  /** That sibling by name: its repository, else its title. */
  waitsOnName: string | null;
  /** Planned and built now; only its merge waits on {@link waitsOn} (YAG-1112). */
  ahead: boolean;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function count(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : null;
}

/** The ticket's place in its split, or null for a ticket no split made. */
export function readSplitPlace(item: Pick<WorkItem, "sourceRef">): SplitPlace | null {
  const ref = item.sourceRef;
  const parentId = text(ref?.splitFrom);
  if (!ref || !parentId) return null;
  return {
    parentId,
    parentTitle: text(ref.splitParentTitle),
    parentKey: text(ref.splitParentKey),
    order: count(ref.splitOrder),
    part: count(ref.splitOrder) ?? count(ref.splitPart),
    total: count(ref.splitTotal),
    beside: ref.splitBeside === true,
    waitsOn: text(ref.splitWaitsOn),
    waitsOnName: text(ref.splitWaitsOnName),
    ahead: ref.splitAhead === true,
  };
}

/** "2 of 3", "3 of 3, built beside the code"; null when the split kept no order. */
export function splitPositionWords(place: SplitPlace): string | null {
  if (place.order === null) return null;
  const of = place.total !== null ? `${place.order} of ${place.total}` : `Ticket ${place.order}`;
  return place.beside ? `${of}, built beside the code` : of;
}

/**
 * "waits for gpxco/gpx-engine to merge" while an earlier sibling's pull
 * request is open and nothing runs on this one. Null for one built ahead,
 * which plans now ({@link splitMergeWords}).
 */
export function splitWaitWords(place: SplitPlace | null): string | null {
  if (!place?.waitsOn || place.ahead) return null;
  return `waits for ${place.waitsOnName ?? "the ticket before it"} to merge`;
}

/** "merges after gpxco/gpx-engine": a ticket built ahead whose merge waits on that one (YAG-1112). */
export function splitMergeWords(place: SplitPlace | null): string | null {
  if (!place?.waitsOn || !place.ahead) return null;
  return `merges after ${place.waitsOnName ?? "the ticket before it"}`;
}

/** The parent as a child names it: `ENG-3033 "Add the value field"`, else what was kept. */
export function splitParentName(place: SplitPlace): string {
  if (place.parentTitle && place.parentKey) return `${place.parentKey} "${place.parentTitle}"`;
  return place.parentTitle ?? place.parentKey ?? "the ticket it came out of";
}

/**
 * The tag a live split's row wears in place of its stage word (YAG-1111): the
 * parent is not closed while its tickets build, and it is not one stage's
 * work either. The pie still draws where its least-advanced ticket stands.
 */
export const SPLIT_LABEL = "Split";

const PART_STAGE_WORDS: Record<WorkPipelineStage, string> = {
  proposed: "proposed",
  plan: "planning",
  build: "building",
  review: "in review",
  qa: "in QA",
  done: "done",
};

/** "part 2 waiting on you", "part 1 building": where the ticket a split's row names stands. */
export function splitPartWords(part: WorkItemSplitPart): string {
  const name = `part ${part.order}`;
  switch (part.state) {
    case "waiting":
      return `${name} waiting on you`;
    case "working":
      return `${name} ${PART_STAGE_WORDS[part.stage]}`;
    default:
      return `${name} ${part.state}`;
  }
}

/** "1 of 3 merged · part 2 waiting on you": a live split's row, after its tag. */
export function splitProgressWords(progress: WorkItemSplitProgress): string {
  const merged = `${progress.merged} of ${progress.total} merged`;
  const lead = splitLeadOf(progress);
  return lead ? `${merged} · ${splitPartWords(lead)}` : merged;
}

/** The pie's ring for a live split: the ring of the ticket its row names. */
export function splitPieMode(progress: WorkItemSplitProgress): StagePieMode {
  switch (splitLeadOf(progress)?.state) {
    case "waiting":
      return "wait";
    case "working":
      return "live";
    case "stopped":
    case "closed":
      return "stop";
    default:
      return "past";
  }
}

/**
 * A split's ticket as its row under the parent's says it (YAG-1113): its part
 * ("part 2 of 3"), what it waits on to start ("after part 1 merges"), and
 * whether its pull request merged by the parent's own count.
 */
export interface SplitPartPlace {
  position: string;
  after: string | null;
  merged: boolean;
}

/** "part 2 of 3", "part 3 of 3, built beside the code", "part 2" when the split kept no count. */
export function splitPartPosition(part: number, total: number | null, beside: boolean): string {
  const of = total !== null && total >= part ? `part ${part} of ${total}` : `part ${part}`;
  return beside ? `${of}, built beside the code` : of;
}

type SplitTicket = Pick<WorkItem, "id" | "sourceRef">;

/**
 * Where one listed ticket of a split stands among the ones listed with it.
 * `siblings` are the split's tickets on the list in split order, this one
 * included; a ticket that kept no part number is numbered by that order.
 */
export function splitPartPlaceOf(
  child: SplitTicket,
  parent: Pick<WorkItem, "sourceRef" | "splitProgress">,
  siblings: readonly SplitTicket[],
): SplitPartPlace {
  const place = readSplitPlace(child);
  const partOf = (ticket: SplitTicket) =>
    readSplitPlace(ticket)?.part ??
    Math.max(1, siblings.findIndex((each) => each.id === ticket.id) + 1);
  const into = parent.sourceRef?.splitInto;
  const total = place?.total ?? (Array.isArray(into) ? into.length : siblings.length);
  const merged =
    parent.splitProgress?.parts.some((part) => part.id === child.id && part.state === "merged") ??
    false;
  // A ticket built ahead (YAG-1112) is under way, and only its merge waits:
  // it says its own state, and a held draft's says what it merges after.
  let after: string | null = null;
  if (place?.waitsOn && !place.ahead && !merged) {
    const before = siblings.find((each) => each.id === place.waitsOn);
    after = before
      ? `after part ${partOf(before)} merges`
      : `after ${place.waitsOnName ?? "the one before it"} merges`;
  }
  return {
    position: splitPartPosition(partOf(child), total, place?.beside ?? false),
    after,
    merged,
  };
}

/**
 * The line a split's ticket prints under its parent: "part 2 of 3 · after
 * part 1 merges", "part 1 of 3 · merged", or its part before its own
 * sentence, "part 1 of 3 · Wright is working".
 */
export function splitPartLine(place: SplitPartPlace, sentence: string): string {
  const what = place.after ?? (place.merged ? "merged" : sentence);
  return what ? `${place.position} · ${what}` : place.position;
}
